/**
 * Display text for a self-serve ad's salary.
 *
 * The wizard stores one of a fixed set of Cyrillic range labels ("1 - 1.5 сая", see
 * PostJob.jsx). Some of them come back from the API with the Cyrillic word replaced by
 * question marks — "1 - 1.5 ???" — which is a charset problem on the storage side, not
 * something the client can prevent.
 *
 * Because the set is closed and only the trailing word is lost, the label can be
 * recovered exactly: match on the numeric part and return the canonical option. This is
 * a display repair, not a guess — a value whose numbers don't match a known option is
 * passed through untouched rather than "corrected" into something it never was.
 *
 * Remove this once the API returns the strings intact.
 */

/** The options offered in the posting wizard, in the order they appear there. */
export const SALARY_RANGES = [
  "0.8 - 1 сая",
  "1 - 1.5 сая",
  "1.5 - 2 сая",
  "2 - 2.5 сая",
  "2.5 - 3 сая",
  "3 - 4 сая",
  "4 - 5 сая",
  "5+ сая",
];

/** "1 - 1.5 сая" -> "1-1.5", so a mangled tail can't affect the comparison. */
const numericKey = (value) =>
  String(value || "")
    .replace(/[^\d.+-]/g, "")
    .replace(/-+$/, "")
    .trim();

const BY_KEY = Object.fromEntries(SALARY_RANGES.map((r) => [numericKey(r), r]));

/**
 * Repair a salary string that has already been written somewhere — a comment posted
 * before this fix existed, for instance. Same rule as below: restore only when the
 * numbers identify one of the wizard's options, otherwise just drop the stray "?"s.
 *
 * @param {string} text
 * @returns {string}
 */
export const repairSalaryLabel = (text) => {
  const raw = String(text || "").trim();
  if (!raw) return "";
  // Strip a trailing currency mark before matching, then put it back.
  const hasTugrik = /₮\s*$/.test(raw);
  const body = raw.replace(/₮\s*$/, "").trim();
  const restored = BY_KEY[numericKey(body)];
  const out = restored || body.replace(/\s*\?+\s*$/, "").trim();
  return hasTugrik ? `${out} ₮` : out;
};

/**
 * @param {{salary?: string, salaryNegotiable?: boolean}} job
 * @returns {string} text ready to render, or "" when there is nothing to say
 */
export const formatEmployerSalary = (job) => {
  if (!job) return "";
  if (job.salaryNegotiable) return "Тохиролцоно";

  const raw = String(job.salary || "").trim();
  if (!raw) return "";

  const restored = BY_KEY[numericKey(raw)];
  if (restored) return `${restored} ₮`;

  // Not one of the wizard's ranges — free text an employer typed, or a value from
  // elsewhere. Show it as-is, minus a trailing run of question marks, which is never
  // information.
  return `${raw.replace(/\s*\?+\s*$/, "").trim()} ₮`;
};

export default formatEmployerSalary;
